import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faChevronDown,
  faChevronRight,
  faToggleOn,
  faToggleOff,
  faRightLeft,
  faBan,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchPersonaLibrary,
  type LibraryPersona,
  type LibraryPersonaAlias,
} from '../api/personaLibrary';
import { repointAlias, setAliasActive, setNodeActive } from '../api/personaTaxonomy';

type Toast = { kind: 'info' | 'error'; text: string };

/** First char of the label uppercased, or '#'. Mirrors the backend grouping. */
function groupLetter(name: string): string {
  const t = name.trim();
  return t ? t[0].toUpperCase() : '#';
}

function errText(e: unknown, fallback: string): string {
  const resp = (e as { response?: { data?: { message?: string } } })?.response;
  return resp?.data?.message ?? fallback;
}

/**
 * Persona Library — the canonical archetype catalog (personas_spec.md §3, §9),
 * the persona analogue of the Skills Library. Reduced to a persona's fields: no
 * category labels and no CV origins; the "demand" column (distinct tenders) is
 * the analogue of a skill's "assigned". Independent of the legacy persona UI.
 */
export default function PersonaLibrary() {
  const { t } = useTranslation();
  const [personas, setPersonas] = useState<LibraryPersona[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingAliasId, setEditingAliasId] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const flash = useCallback((next: Toast) => {
    setToast(next);
    window.setTimeout(() => setToast((cur) => (cur === next ? null : cur)), 4000);
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    fetchPersonaLibrary()
      .then((lib) => setPersonas(lib.groups.flatMap((g) => g.personas)))
      .catch((e) => setLoadError(errText(e, t('personas.loadError', 'Could not load persona library.'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { reload(); }, [reload]);

  const activePersonas = useMemo(() => personas.filter((p) => p.status === 'ACTIVE'), [personas]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = q
      ? personas.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.aliases.some((a) => a.text.toLowerCase().includes(q)),
        )
      : personas;
    const byLetter = new Map<string, LibraryPersona[]>();
    for (const p of matched) {
      const letter = groupLetter(p.name);
      let bucket = byLetter.get(letter);
      if (!bucket) { bucket = []; byLetter.set(letter, bucket); }
      bucket.push(p);
    }
    return [...byLetter.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([letter, list]) => ({
        letter,
        personas: [...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
      }));
  }, [personas, search]);

  const toggleExpand = (id: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleNode = async (persona: LibraryPersona) => {
    const active = persona.status !== 'ACTIVE';
    try {
      await setNodeActive(persona.id, active);
      reload();
    } catch (e) {
      flash({ kind: 'error', text: errText(e, t('personas.actionFailed', 'Action failed.')) });
    }
  };

  const toggleAlias = async (alias: LibraryPersonaAlias) => {
    const active = alias.status !== 'ACTIVE';
    try {
      await setAliasActive(alias.id, active);
      reload();
    } catch (e) {
      flash({ kind: 'error', text: errText(e, t('personas.actionFailed', 'Action failed.')) });
    }
  };

  const doRepoint = async (alias: LibraryPersonaAlias, targetId: string) => {
    setEditingAliasId(null);
    try {
      await repointAlias(alias.id, targetId);
      reload();
    } catch (e) {
      flash({ kind: 'error', text: errText(e, t('personas.actionFailed', 'Action failed.')) });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('personas.libraryTitle', 'Persona Library')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('personas.librarySubtitle', 'The canonical archetype catalog — one dominant persona per tender, with demand across the stream.')}
        </p>
      </div>

      <div className="rounded-[14px] border border-gray-200 bg-white shadow-sm">
        {/* Toolbar */}
        <div className="flex items-center gap-3 border-b border-gray-100 p-3">
          <div className="relative max-w-[340px] flex-1">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('personas.search', 'Search personas and aliases')}
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
          </div>
        </div>

        {/* Column header */}
        <div className="grid grid-cols-[minmax(0,1fr)_96px_36px] items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
          <span>{t('personas.colPersona', 'Persona')}</span>
          <span className="text-right">{t('personas.demand', 'Demand')}</span>
          <span aria-hidden="true" />
        </div>

        {loadError && (
          <div className="m-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">{loadError}</div>
        )}
        {loading ? (
          <p className="p-8 text-center text-sm text-gray-400">{t('personas.loading', 'Loading…')}</p>
        ) : grouped.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-400">{t('personas.noResults', 'No personas found.')}</p>
        ) : (
          grouped.map((group) => (
            <div key={group.letter}>
              <div className="flex items-center gap-2 bg-gray-50/70 px-4 py-1.5 text-xs font-semibold text-gray-500">
                {group.letter}
                <span className="text-gray-400">({group.personas.length})</span>
              </div>
              <ul>
                {group.personas.map((persona) => (
                  <PersonaRow
                    key={persona.id}
                    persona={persona}
                    activePersonas={activePersonas}
                    expanded={expanded.has(persona.id)}
                    onToggleExpand={() => toggleExpand(persona.id)}
                    onToggleNode={() => toggleNode(persona)}
                    onToggleAlias={toggleAlias}
                    editingAliasId={editingAliasId}
                    setEditingAliasId={setEditingAliasId}
                    onRepointSelect={doRepoint}
                  />
                ))}
              </ul>
            </div>
          ))
        )}
      </div>

      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg px-4 py-2 text-sm text-white ${toast.kind === 'error' ? 'bg-red-600' : 'bg-gray-800'}`}
          role="status"
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

interface PersonaRowProps {
  persona: LibraryPersona;
  activePersonas: LibraryPersona[];
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleNode: () => void;
  onToggleAlias: (alias: LibraryPersonaAlias) => void;
  editingAliasId: string | null;
  setEditingAliasId: (id: string | null) => void;
  onRepointSelect: (alias: LibraryPersonaAlias, targetId: string) => void;
}

function PersonaRow({
  persona, activePersonas, expanded, onToggleExpand, onToggleNode,
  onToggleAlias, editingAliasId, setEditingAliasId, onRepointSelect,
}: PersonaRowProps) {
  const { t } = useTranslation();
  const inactive = persona.status !== 'ACTIVE';
  const aliases = [...persona.aliases].sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));

  return (
    <li className="border-b border-gray-50 last:border-0">
      <div
        className="grid cursor-pointer grid-cols-[minmax(0,1fr)_96px_36px] items-center gap-2 px-4 py-2.5 hover:bg-gray-50"
        onClick={onToggleExpand}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleExpand(); } }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} className="h-3 w-3 shrink-0 text-gray-400" />
          <span className={`truncate text-sm font-medium ${inactive ? 'text-gray-400' : 'text-gray-900'}`}>{persona.name}</span>
          {inactive && (
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase text-gray-500">
              {t('personas.deprecated', 'deprecated')}
            </span>
          )}
        </div>
        <span className="text-right text-sm text-gray-600">{persona.demand}</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleNode(); }}
          aria-label={inactive ? t('personas.reactivate', 'Reactivate') : t('personas.deprecate', 'Deprecate')}
          className="justify-self-center"
        >
          <FontAwesomeIcon icon={inactive ? faToggleOff : faToggleOn} className={`h-4 w-4 ${inactive ? 'text-gray-300' : 'text-green-600'}`} />
        </button>
      </div>

      {expanded && (
        <div className="space-y-1 bg-gray-50/60 px-4 py-2">
          {aliases.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-400">
              {t('personas.noAliases', 'No aliases.')}
            </div>
          ) : (
            aliases.map((alias) => (
              <AliasRow
                key={alias.id}
                alias={alias}
                currentNodeId={persona.id}
                activePersonas={activePersonas}
                editing={editingAliasId === alias.id}
                setEditing={(on) => setEditingAliasId(on ? alias.id : null)}
                onRepointSelect={(targetId) => onRepointSelect(alias, targetId)}
                onToggleAlias={() => onToggleAlias(alias)}
              />
            ))
          )}
        </div>
      )}
    </li>
  );
}

interface AliasRowProps {
  alias: LibraryPersonaAlias;
  currentNodeId: string;
  activePersonas: LibraryPersona[];
  editing: boolean;
  setEditing: (on: boolean) => void;
  onRepointSelect: (targetId: string) => void;
  onToggleAlias: () => void;
}

function AliasRow({ alias, currentNodeId, activePersonas, editing, setEditing, onRepointSelect, onToggleAlias }: AliasRowProps) {
  const { t } = useTranslation();
  const deprecated = alias.status !== 'ACTIVE';
  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-white px-3 py-1.5">
      <code className={`flex-1 truncate text-xs ${deprecated ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{alias.text}</code>
      {deprecated && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-500">{t('personas.deprecated', 'deprecated')}</span>}
      {editing ? (
        <select

          defaultValue=""
          onChange={(e) => e.target.value && onRepointSelect(e.target.value)}
          onBlur={() => setEditing(false)}
          className="rounded border border-gray-200 px-2 py-1 text-xs"
        >
          <option value="" disabled>{t('personas.repointTo', 'Repoint to…')}</option>
          {activePersonas.filter((p) => p.id !== currentNodeId).map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      ) : (
        <button type="button" onClick={() => setEditing(true)} aria-label={t('personas.repoint', 'Repoint')} className="text-gray-400 hover:text-brand-600">
          <FontAwesomeIcon icon={faRightLeft} className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={onToggleAlias}
        aria-label={deprecated ? t('personas.reactivate', 'Reactivate') : t('personas.deprecate', 'Deprecate')}
        className={deprecated ? 'text-green-600' : 'text-red-500'}
      >
        <FontAwesomeIcon icon={deprecated ? faRotateLeft : faBan} className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
